import { LitElement, html, nothing } from "lit";

import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import type { CreatePostParams } from "api-contracts/rpc/create-post";
import type { UpdatePostParams } from "api-contracts/rpc/update-post";
import type { GetMySubscriptionTierResult } from "api-contracts/rpc/get-my-subscription-tier";
import { DataQueryController } from "ui-common/data-query-controller";
import "ui-components/button/wow-button";
import "ui-components/confirmation/wow-confirmation";
import "ui-components/expandible-switch/expandible-switch";
import "ui-components/faq-block/faq-block";
import "../../components/confirmation-edit-draft-discard/confirmation-edit-draft-discard";
import type { Option } from "ui-components/form-elements/autocomplete-multi-select";
import "ui-components/form-elements/autocomplete-multi-select/autocomplete-multi-select";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/switch/switch";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/media-uploader/media-uploader";
import "ui-components/modal/wow-modal";
import "ui-components/premium-content-controls/premium-content-controls";
import { NotificationService } from "ui-components/notification";
import { validateUrl } from "helpers/url";
import { getMySubscriptionTierQueryOptions } from "ui-common/queries/subscription-tier";
import { featureFlag } from "ui-components/wow-feature/wow-feature";
import { isStripePaywallEnabled } from "../../config/stripe-paywall-enabled-creators";
import { appContext } from "ui-common/appContext";

import { createPostModalStyles, thumbnailStyles } from "./create-post-modal.css";

import "../../components/preview-publish-modal/preview-publish-modal";
import type { PreviewModalData } from "../../components/preview-publish-modal/preview-publish-modal";
import { brandsQueryOptions } from "../../queries/brands";
import { getPostQueryOptions } from "../../queries/get-post";
import { profileDataQueryOptions } from "../../queries/profile-data";
import { worldsQueryOptions } from "../../queries/worlds";
import { createPostMutation, updatePostMutation } from "../../services/post-mutations";
import { t, translate } from "../../translations/i18n";

// FAQ block interface
interface FaqBlock {
  id: string;
  question: string;
  answer: string;
}

@customElement("create-post-modal")
export class CreatePostModal extends LitElement {
  static override styles = [createPostModalStyles, thumbnailStyles];

  @property({ type: String })
  postId?: string;

  @state()
  private description = "";

  @state()
  private selectedTheme = "";

  @state()
  private selectedExistingWorldIds: string[] = [];

  @state()
  private selectedNewWorldNames: string[] = [];

  @state()
  private selectedBrandIds: string[] = [];

  @state()
  private brandSearchTerm = "";

  @state()
  private switches = {
    ctaButton: false,
    discount: false,
    brand: false,
    faq: false,
  };

  @state()
  private ctaData = {
    callToAction: "",
    url: "",
  };

  @state()
  private discountData = {
    code: "",
  };

  @state()
  private faqBlocks: FaqBlock[] = [];

  @state()
  private mediaFileType?: "image" | "video";

  @state()
  private mediaFileId?: string;

  @state()
  private mediaFileUrl?: string;

  @state()
  private showPreviewModal = false;

  @state()
  private previewData?: PreviewModalData;

  @state()
  private showCloseConfirmation = false;

  @state()
  private isFileUploadActive = false;

  @state()
  private isSubmittingDraft = false;

  @state()
  private premiumTier: "free" | "premium" = "free";

  @state()
  private enableOneTimePurchase = false;

  @state()
  private oneTimePriceCents?: number;

  // Data query for worlds
  private worldsQuery = new DataQueryController(this, () => worldsQueryOptions);

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);

  private subscriptionTierQuery = new DataQueryController<GetMySubscriptionTierResult>(this, () =>
    getMySubscriptionTierQueryOptions(),
  );

  private getUserPrimaryColor(): string | undefined {
    const profileData = this.profileDataQuery.result.data;
    if (!profileData) return undefined;

    // Priority: customColors > theme > fallback secondaryColor
    return (
      profileData.theme?.primaryColor || profileData.primaryColor || profileData.customColors?.primary || undefined
    );
  }

  private getUserSecondaryColor(): string | undefined {
    const profileData = this.profileDataQuery.result.data;
    if (!profileData) return undefined;

    // Priority: customColors > theme > fallback secondaryColor
    return (
      profileData.theme?.secondaryColor ||
      profileData.secondaryColor ||
      profileData.customColors?.secondary ||
      undefined
    );
  }

  // Data query for brands (with search term and selected IDs)
  private brandsQuery = new DataQueryController(this, () =>
    brandsQueryOptions(this.brandSearchTerm, this.selectedBrandIds),
  );

  @state()
  private isFormInitialized = false;

  // Store original form state for comparison in edit mode
  private originalFormState?: {
    description: string;
    mediaFileId?: string;
    selectedExistingWorldIds: string[];
    selectedNewWorldNames: string[];
    selectedBrandIds: string[];
    ctaData: { callToAction: string; url: string };
    discountData: { code: string };
    faqBlocks: FaqBlock[];
    switches: {
      ctaButton: boolean;
      discount: boolean;
      brand: boolean;
      faq: boolean;
    };
    premiumTier: "free" | "premium";
    enableOneTimePurchase: boolean;
    oneTimePriceCents?: number;
  };

  // Data query for editing post (only if postId is provided)
  private postQuery = new DataQueryController(this, () => getPostQueryOptions({ postId: this.postId }));

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    if (!this.isFormInitialized && this.postQuery.result.isSuccess) {
      this.populateFormFromPost();
      this.isFormInitialized = true;
    }
  }

  private populateFormFromPost() {
    const { data: post, isPending, error } = this.postQuery.result;

    if (post && !isPending && !error) {
      // Populate all form fields from the post data
      this.description = post.teaser || "";
      this.mediaFileId = post.mediaFileId || undefined;
      this.mediaFileUrl = post.mediaUrl || undefined;
      this.mediaFileType = post.mediaFileType || undefined;

      // Populate worlds
      this.selectedExistingWorldIds = post.worlds.map((w) => w.id);
      this.selectedNewWorldNames = []; // Existing posts won't have new worlds

      // Populate brands
      this.selectedBrandIds = post.brands.map((b) => b.id);

      // Populate CTA data
      if (post.ctaData) {
        this.switches = { ...this.switches, ctaButton: true };
        this.ctaData = {
          callToAction: post.ctaData.callToAction,
          url: post.ctaData.url,
        };
      }

      // Populate discount data
      if (post.discountData) {
        this.switches = { ...this.switches, discount: true };
        this.discountData = {
          code: post.discountData.code,
        };
      }

      // Populate FAQ blocks
      if (post.faqBlocks.length > 0) {
        this.switches = { ...this.switches, faq: true };
        this.faqBlocks = post.faqBlocks.map((faq) => ({
          id: faq.id,
          question: faq.question,
          answer: faq.answer,
        }));
      }

      // Set brand switch if brands are present
      if (post.brands.length > 0) {
        this.switches = { ...this.switches, brand: true };
      }

      // Populate premium tier data
      this.premiumTier = post.contentTier || "free";

      // Check if post has one-time purchase enabled
      if (post.oneTimeProduct) {
        this.enableOneTimePurchase = true;
        this.oneTimePriceCents = post.oneTimeProduct.priceCents;
      }

      // Store original state for comparison
      this.originalFormState = {
        description: this.description,
        mediaFileId: this.mediaFileId,
        selectedExistingWorldIds: [...this.selectedExistingWorldIds],
        selectedNewWorldNames: [...this.selectedNewWorldNames],
        selectedBrandIds: [...this.selectedBrandIds],
        ctaData: { ...this.ctaData },
        discountData: { ...this.discountData },
        faqBlocks: this.faqBlocks.map((faq) => ({ ...faq })),
        switches: { ...this.switches },
        premiumTier: this.premiumTier,
        enableOneTimePurchase: this.enableOneTimePurchase,
        oneTimePriceCents: this.oneTimePriceCents,
      };
    }
  }

  private get isEditMode(): boolean {
    return !!this.postId;
  }

  private hasUnsavedChanges(): boolean {
    // Edit mode: compare with original state
    if (this.isEditMode && this.originalFormState) {
      const arraysEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, idx) => val === sortedB[idx]);
      };

      const faqsEqual = (a: FaqBlock[], b: FaqBlock[]) =>
        a.length === b.length &&
        a.every((blockA, idx) => {
          const blockB = b[idx];
          return blockB && blockA.question === blockB.question && blockA.answer === blockB.answer;
        });

      const originalFormState = this.originalFormState;

      const changes = [
        this.description !== originalFormState.description,
        this.mediaFileId !== originalFormState.mediaFileId,
        !arraysEqual(this.selectedExistingWorldIds, originalFormState.selectedExistingWorldIds),
        !arraysEqual(this.selectedNewWorldNames, originalFormState.selectedNewWorldNames),
        !arraysEqual(this.selectedBrandIds, originalFormState.selectedBrandIds),
        this.ctaData.callToAction !== originalFormState.ctaData.callToAction,
        this.ctaData.url !== originalFormState.ctaData.url,
        this.discountData.code !== originalFormState.discountData.code,
        !faqsEqual(this.faqBlocks, originalFormState.faqBlocks),
        this.switches.ctaButton !== originalFormState.switches.ctaButton,
        this.switches.discount !== originalFormState.switches.discount,
        this.switches.brand !== originalFormState.switches.brand,
        this.switches.faq !== originalFormState.switches.faq,
        this.premiumTier !== originalFormState.premiumTier,
        this.enableOneTimePurchase !== originalFormState.enableOneTimePurchase,
        this.oneTimePriceCents !== originalFormState.oneTimePriceCents,
      ];

      return changes.some(Boolean);
    }

    // Create mode: check if any field has content
    const hasContent = [
      this.description.trim() !== "",
      this.selectedExistingWorldIds.length > 0,
      this.selectedNewWorldNames.length > 0,
      this.ctaData.callToAction.trim() !== "",
      this.ctaData.url.trim() !== "",
      this.discountData.code.trim() !== "",
      this.faqBlocks.some((item) => item.question.trim() !== "" || item.answer.trim() !== ""),
      this.mediaFileId !== undefined,
      this.selectedBrandIds.length > 0,
      this.premiumTier === "premium",
      this.enableOneTimePurchase,
    ];

    return hasContent.some(Boolean);
  }

  private canModalClose(): { allowed: boolean; reason?: string } {
    if (this.isFileUploadActive) {
      return { allowed: false, reason: "file-upload-active" };
    }

    if (this.hasUnsavedChanges()) {
      return { allowed: false, reason: "has-unsaved-changes" };
    }

    return { allowed: true };
  }

  private actualClose() {
    this.showCloseConfirmation = false;
    this.dispatchEvent(
      new CustomEvent("modal-close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private closeModal = () => {
    const canClose = this.canModalClose();

    if (canClose.allowed) {
      this.actualClose();
    } else {
      // Handle why it can't close
      switch (canClose.reason) {
        case "has-unsaved-changes":
          this.showCloseConfirmation = true;
          break;
        case "file-upload-active":
          // Do nothing - modal stays open
          break;
      }
    }
  };

  private handleCloseRequested = () => {
    this.closeModal();
  };

  private handleDescriptionChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.description = target.value;
    // Clear validation state when user starts typing
    target.setCustomValidity("");
  };

  private handleCtaCallToActionChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.ctaData = {
      ...this.ctaData,
      callToAction: target.value,
    };
    // Clear validation state when user starts typing
    target.setCustomValidity("");
  };

  private handleCtaUrlChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.ctaData = {
      ...this.ctaData,
      url: target.value,
    };
    // Clear validation state when user starts typing
    target.setCustomValidity("");
  };

  private handleCtaExpandedChange = (event: CustomEvent) => {
    this.switches = {
      ...this.switches,
      ctaButton: event.detail.expanded,
    };
  };

  private handleDiscountExpandedChange = (event: CustomEvent) => {
    this.switches = {
      ...this.switches,
      discount: event.detail.expanded,
    };
  };

  private handleDiscountCodeChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.discountData = {
      ...this.discountData,
      code: target.value,
    };
    // Clear validation state when user starts typing
    target.setCustomValidity("");
  };

  private handleBrandExpandedChange = (event: CustomEvent) => {
    this.switches = {
      ...this.switches,
      brand: event.detail.expanded,
    };
  };

  // Handle brand search input
  private handleBrandSearch = (event: CustomEvent) => {
    this.brandSearchTerm = event.detail.searchTerm;
  };

  private handleFaqExpandedChange = (event: CustomEvent) => {
    const isExpanded = event.detail.expanded;

    this.switches = {
      ...this.switches,
      faq: isExpanded,
    };

    // Auto-add first FAQ when expanding and no FAQs exist
    if (isExpanded && this.faqBlocks.length === 0) {
      this.addFaqBlock();
    }
  };

  // FAQ block management
  private addFaqBlock = () => {
    const newBlock: FaqBlock = {
      id: `faq-${Date.now()}-${Math.random()}`,
      question: "",
      answer: "",
    };
    this.faqBlocks = [...this.faqBlocks, newBlock];
  };

  private removeFaqBlock = (blockId: string) => {
    // Simple data removal - animation is handled by the component itself
    this.faqBlocks = this.faqBlocks.filter((block) => block.id !== blockId);
  };

  private handleFaqQuestionChange = (event: CustomEvent) => {
    const { blockId, value } = event.detail;
    this.faqBlocks = this.faqBlocks.map((block) => (block.id === blockId ? { ...block, question: value } : block));
  };

  private handleFaqAnswerChange = (event: CustomEvent) => {
    const { blockId, value } = event.detail;
    this.faqBlocks = this.faqBlocks.map((block) => (block.id === blockId ? { ...block, answer: value } : block));
  };

  private handleFaqDeleteRequested = (event: CustomEvent) => {
    const { blockId } = event.detail;
    this.removeFaqBlock(blockId);
  };

  // Transform worlds data to Option format with filtering and create option
  private get worldOptions(): Option[] {
    const { data } = this.worldsQuery.result;
    const existingWorlds: Option[] =
      data?.worlds?.map((world) => ({
        value: world.id,
        label: world.name,
      })) || [];

    // Convert new world names to options with special prefix
    const newWorldOptions: Option[] = this.selectedNewWorldNames.map((name) => ({
      value: `new:${name}`,
      label: name,
      chipLabel: name,
    }));

    // Combine all options
    const allOptions: Option[] = [...existingWorlds, ...newWorldOptions];

    // Filter options based on search term (if any)
    let filteredOptions = allOptions;
    if (this.worldSearchTerm.trim()) {
      filteredOptions = allOptions.filter((option) =>
        option.label.toLowerCase().includes(this.worldSearchTerm.toLowerCase()),
      );
    }

    // Get all currently selected values for display
    const allSelectedValues = [
      ...this.selectedExistingWorldIds,
      ...this.selectedNewWorldNames.map((name) => `new:${name}`),
    ];
    const selectedOptions = allSelectedValues
      .map((value) => allOptions.find((option) => option.value === value))
      .filter(Boolean) as Option[];

    // Combine filtered and selected options (removing duplicates)
    const availableOptions = [
      ...filteredOptions,
      ...selectedOptions.filter((selected) => !filteredOptions.some((filtered) => filtered.value === selected.value)),
    ];

    // Add create option if there's a search term and no exact match
    const searchTerm = this.worldSearchTerm.trim();
    if (searchTerm) {
      const hasExactMatch = availableOptions.some((option) => option.label.toLowerCase() === searchTerm.toLowerCase());

      if (!hasExactMatch) {
        availableOptions.push({
          value: `new:${searchTerm}`,
          chipLabel: searchTerm,
          label: translate("createPost.createWorld", { name: searchTerm }),
          endIcon: html`<ph-plus size="16"></ph-plus>`,
        });
      }
    }

    return availableOptions;
  }

  // Transform brands data to Option format with filtering and create option
  private get brandOptions(): Option[] {
    const brands = this.brandsQuery.result.data || [];

    // Convert to Option format (brands already filtered by search in the query)
    const brandOptions: Option[] = brands.map(
      (brand): Option => ({
        value: brand.id,
        label: brand.name,
        // Note: Brand logos can be added here when available
        // startIcon: brand.logo ? html`<img src="${brand.logo}" width="16" height="16" alt="${brand.label} logo">` : undefined
      }),
    );

    return brandOptions;
  }

  @state()
  private worldSearchTerm = "";

  // Handle world selection (including create option)
  private handleWorldsChange = (event: CustomEvent) => {
    const selectedValues: string[] = event.detail.value;

    // Separate existing world IDs from new world names based on value prefix
    this.selectedExistingWorldIds = selectedValues.filter((v) => !v.startsWith("new:") && v !== "create-new-world");
    this.selectedNewWorldNames = selectedValues.filter((v) => v.startsWith("new:")).map((v) => v.replace("new:", ""));

    // Handle create option
    if (selectedValues.includes("create-new-world")) {
      const newName = this.worldSearchTerm.trim();
      if (newName && !this.selectedNewWorldNames.includes(newName)) {
        this.selectedNewWorldNames = [...this.selectedNewWorldNames, newName];
      }
      this.worldSearchTerm = "";
    }
  };

  // Handle brand selection (including create option)
  private handleBrandsChange = (event: CustomEvent) => {
    const selectedValues: string[] = event.detail.value;

    // Check if create option was selected
    if (selectedValues.includes("create-new-brand")) {
      // Handle create new brand logic here if needed
      console.log("Create new brand:", this.brandSearchTerm);

      // For now, just filter out the create option
      this.selectedBrandIds = selectedValues.filter((v) => v !== "create-new-brand");
    } else {
      this.selectedBrandIds = selectedValues;
    }
  };

  // Handle world search input
  private handleWorldSearch = (event: CustomEvent) => {
    this.worldSearchTerm = event.detail.searchTerm;
  };

  private handleTierChange = (event: CustomEvent) => {
    console.log("[CREATE POST MODAL] handleTierChange called with tier:", event.detail.tier);
    this.premiumTier = event.detail.tier;
    console.log("[CREATE POST MODAL] premiumTier set to:", this.premiumTier);
  };

  private handleOneTimePurchaseChange = (event: CustomEvent) => {
    const { enabled, priceCents } = event.detail;
    this.enableOneTimePurchase = enabled;
    this.oneTimePriceCents = priceCents;
  };

  private handleFileUploadStart = () => {
    this.isFileUploadActive = true;
  };

  private handleFileUploadCancel = () => {
    this.isFileUploadActive = false;
  };

  // Handle media upload change
  private handleMediaChange = (event: CustomEvent) => {
    this.mediaFileId = event.detail.fileId || undefined;
    this.mediaFileUrl = event.detail.imageUrl || undefined;
    this.mediaFileType = event.detail.fileType || undefined;
    this.isFileUploadActive = false;

    // Clear any validation errors
    const mediaElement = this.renderRoot.querySelector<HTMLInputElement>("#media");
    if (mediaElement) {
      mediaElement.setCustomValidity("");
    }
  };

  private handleMediaError = (event: CustomEvent) => {
    const { error } = event.detail;
    NotificationService.error(error);
    this.isFileUploadActive = false;
  };

  private handleContinueEditing = () => {
    this.showCloseConfirmation = false;
  };

  private async handleSaveAsDraft() {
    this.showCloseConfirmation = false;

    this.previewData = this.setPreviewData();

    if (!this.previewData) {
      this.showCloseConfirmation = false;
      return;
    }

    const isEditMode = !!this.previewData.postId;

    try {
      const basePostData: Omit<CreatePostParams, "description"> | Omit<UpdatePostParams, "postId" & "teaser"> = {
        worldIds: this.previewData.selectedExistingWorldIds,
        newWorldNames: this.previewData.selectedNewWorldNames,
        brandIds: this.previewData.selectedBrandIds,
        ctaData: this.previewData.switches.ctaButton && this.previewData.ctaData ? this.previewData.ctaData : undefined,
        discountData:
          this.previewData.switches.discount && this.previewData.discountData
            ? this.previewData.discountData
            : undefined,
        faqBlocks: this.previewData.switches.faq ? this.previewData.faqBlocks : [],
        fileId: this.previewData.mediaFileId,
        // Post becomes a draft
        cover: false,
        startDate: undefined,
        contentTier: this.previewData.premiumTier,
        oneTimePriceCents: this.previewData.enableOneTimePurchase ? this.previewData.oneTimePriceCents : undefined,
      };

      // Use appropriate mutation based on whether we're creating or updating

      const result = isEditMode
        ? await updatePostMutation.mutate({
            postId: this.previewData.postId!,
            teaser: this.previewData.description,
            ...basePostData,
          })
        : await createPostMutation.mutate({
            description: this.previewData.description,
            ...basePostData,
          });
      // The mutation returns the RPC result directly
      if (result && result.postId) {
        NotificationService.show({
          message: translate(isEditMode ? "editPost.updateSuccess" : "createPost.createSuccess"),
          severity: "success",
        });

        // Emit success event to close both modals
        this.dispatchEvent(
          new CustomEvent("publish-success", {
            bubbles: true,
            composed: true,
          }),
        );
        this.dispatchEvent(
          new CustomEvent("modal-close", {
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        NotificationService.show({
          message: translate(isEditMode ? "editPost.updateError" : "createPost.createError"),
          severity: "error",
        });
      }
    } catch (error) {
      const actionText = isEditMode ? "updating" : "creating";
      console.error(`Error ${actionText} post:`, error);
      NotificationService.show({
        message: translate(isEditMode ? "editPost.updateError" : "createPost.createError"),
        severity: "error",
      });
    }
  }

  private handleDiscardChanges = () => {
    this.showCloseConfirmation = false;
    this.dispatchEvent(
      new CustomEvent("modal-close", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  private handleConfirmationClose = () => {
    this.showCloseConfirmation = false;
  };

  private setPreviewData() {
    // Validate required fields following platform patterns
    let isInvalid = false;

    // Clear any previous validation states
    const clearValidation = (selector: string) => {
      const element = this.renderRoot.querySelector<HTMLInputElement>(selector);
      if (element) {
        element.setCustomValidity("");
      }
    };

    clearValidation("#worlds");
    clearValidation("#file");
    clearValidation("#cta-call-to-action");
    clearValidation("#cta-url");
    clearValidation("#discount-code");

    const totalSelectedWorlds = this.selectedExistingWorldIds.length + this.selectedNewWorldNames.length;
    if (totalSelectedWorlds === 0) {
      const worlds = this.renderRoot.querySelector<HTMLInputElement>("#worlds");
      if (worlds) {
        worlds.setCustomValidity(translate("createPost.worldsRequired"));
        worlds.reportValidity();
      }
      isInvalid = true;
    }

    if (!this.mediaFileUrl && !this.mediaFileId) {
      const media = this.renderRoot.querySelector<HTMLInputElement>("#media");
      if (media) {
        media.setCustomValidity(translate("createPost.mediaRequired"));
        media.reportValidity();
      }
      isInvalid = true;
    }

    if (this.switches.ctaButton) {
      if (!this.ctaData.callToAction || this.ctaData.callToAction.trim() === "") {
        const ctaAction = this.renderRoot.querySelector<HTMLInputElement>("#cta-call-to-action");
        if (ctaAction) {
          ctaAction.setCustomValidity(translate("createPost.ctaCallToActionRequired"));
          ctaAction.reportValidity();
        }
        isInvalid = true;
      }

      // Check if URL is provided and valid
      if (!this.ctaData.url || this.ctaData.url.trim() === "") {
        const ctaUrl = this.renderRoot.querySelector<HTMLInputElement>("#cta-url");
        if (ctaUrl) {
          ctaUrl.setCustomValidity(translate("createPost.ctaUrlRequired"));
          ctaUrl.reportValidity();
        }
        isInvalid = true;
      } else {
        const ctaUrl = this.renderRoot.querySelector<HTMLInputElement>("#cta-url");
        let urlToValidate = this.ctaData.url.trim();

        if (!/^https?:\/\//i.test(urlToValidate)) {
          urlToValidate = "https://" + urlToValidate;
        }

        if (!validateUrl(urlToValidate)) {
          if (ctaUrl) {
            ctaUrl.setCustomValidity(translate("createPost.invalidCtaUrl"));
            ctaUrl.reportValidity();
          }
          isInvalid = true;
        } else {
          if (ctaUrl) {
            ctaUrl.setCustomValidity("");
          }
          this.ctaData = { ...this.ctaData, url: urlToValidate };
        }
      }
    }

    if (this.switches.discount) {
      if (!this.discountData.code || this.discountData.code.trim() === "") {
        const discountCode = this.renderRoot.querySelector<HTMLInputElement>("#discount-code");
        if (discountCode) {
          discountCode.setCustomValidity(translate("createPost.discountCodeRequired"));
          discountCode.reportValidity();
        }
        isInvalid = true;
      }
    }

    const descriptionElement = this.shadowRoot?.querySelector<HTMLInputElement>("#description");
    if (descriptionElement?.value == "") {
      descriptionElement.setCustomValidity(translate("createPost.descriptionRequired"));
      isInvalid = true;
    }

    if (isInvalid) {
      return;
    }
    // Prepare data for preview modal
    return {
      scheduledDateTime: this.postQuery.result.data?.startDate as string,
      description: this.description,
      mediaFileId: this.mediaFileId ?? "",
      mediaFileUrl: this.mediaFileUrl,
      mediaFileType: this.mediaFileType,
      selectedExistingWorldIds: this.selectedExistingWorldIds,
      selectedNewWorldNames: this.selectedNewWorldNames,
      selectedBrandIds: this.selectedBrandIds,
      ctaData: this.switches.ctaButton && this.ctaData.callToAction && this.ctaData.url ? this.ctaData : undefined,
      discountData: this.switches.discount && this.discountData.code ? this.discountData : undefined,
      faqBlocks: this.switches.faq ? this.faqBlocks : [],
      switches: this.switches,
      postId: this.postId, // Pass postId to indicate edit mode
      isCurrentlyCover: this.isEditMode && this.profileDataQuery.result.data?.coverPost?.id === this.postId,
      premiumTier: this.premiumTier,
      enableOneTimePurchase: this.enableOneTimePurchase,
      oneTimePriceCents: this.oneTimePriceCents,
    };
  }

  private handlePreviewPublish = async () => {
    console.log("[CREATE POST MODAL] handlePreviewPublish - premiumTier:", this.premiumTier);
    this.previewData = this.setPreviewData();
    console.log("[CREATE POST MODAL] previewData.premiumTier:", this.previewData?.premiumTier);

    // Show preview modal
    this.showPreviewModal = true;
  };

  private handlePreviewModalClose = () => {
    this.showPreviewModal = false;
  };

  private handlePublishSuccess = () => {
    // Close both modals after successful creation
    this.showPreviewModal = false;
    this.actualClose();
  };

  private getWorldNames(): string[] {
    const worldsData = this.worldsQuery.result.data;
    const existingWorldNames = this.selectedExistingWorldIds
      .map((id) => worldsData?.worlds?.find((w) => w.id === id)?.name)
      .filter(Boolean) as string[];

    return [...existingWorldNames, ...this.selectedNewWorldNames];
  }

  private renderMediaUpload() {
    const worldNames = this.getWorldNames();

    return html`
      <div class="media-upload-container">
        <media-uploader
          id="media"
          @change=${this.handleMediaChange}
          @crop-started=${this.handleFileUploadStart}
          @cancel=${this.handleFileUploadCancel}
          @error=${this.handleMediaError}
          viewport="post"
          required
          currentFileUrl=${this.mediaFileUrl}
          currentFileType=${this.mediaFileType}
          name="media"
          context="cover_image"
          slot="content"
          uploadSubtitle="Upload image or video"
        ></media-uploader>
        <div class="thumbnail-overlay">
          <!-- Worlds at top -->
          ${worldNames.length > 0 ? html` <div class="worlds-text">${worldNames.join(", ")}</div> ` : ""}
          <div class="overlay-content">
            ${this.description
              ? html`<expand-text class="overlay-description" no-more disabled>${this.description}</expand-text>`
              : nothing}
            ${this.switches.faq
              ? html` <div class="overlay-faq-text">${t("previewPublish.frequentlyAskedQuestions")}</div> `
              : nothing}
            ${this.switches.ctaButton && this.ctaData
              ? html`
                  <user-theme-button
                    class="cta-button"
                    size="small"
                    full-width
                    .primaryColor=${this.getUserPrimaryColor()}
                    .secondaryColor=${this.getUserSecondaryColor()}
                    .interactive=${false}
                    >${this.ctaData.callToAction}</user-theme-button
                  >
                `
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private renderDescriptionField() {
    return html`
      <div class="form-field">
        <wow-textarea
          id="description"
          .value=${this.description}
          @input=${this.handleDescriptionChange}
          maxlength="750"
          rows="3"
          placeholder="${t("createPost.description")}"
        ></wow-textarea>
      </div>
    `;
  }

  private renderWorldsSelect() {
    const { isPending } = this.worldsQuery.result;

    // Combine all selected values for the component
    const allSelectedValues = [
      ...this.selectedExistingWorldIds,
      ...this.selectedNewWorldNames.map((name) => `new:${name}`),
    ];

    return html`
      <div class="form-field">
        <wow-autocomplete-multi-select
          id="worlds"
          placeholder=${t("createPost.chooseWorlds")}
          loading-text=${t("createPost.loadingWorlds")}
          no-results-text=${t("createPost.noWorldsFound")}
          .options=${this.worldOptions}
          .loading=${isPending}
          .selectedValues=${allSelectedValues}
          .searchTerm=${this.worldSearchTerm}
          @change=${this.handleWorldsChange}
          @search=${this.handleWorldSearch}
          required
        >
          <label slot="label" class="field-label">${t("createPost.chooseWorlds")}</label>
        </wow-autocomplete-multi-select>
      </div>
    `;
  }

  private renderFaqBlocks() {
    return repeat(
      this.faqBlocks,
      (block) => block.id,
      (block, index) => html`
        <faq-block
          .blockNumber=${index + 1}
          .blockId=${block.id}
          .question=${block.question}
          .answer=${block.answer}
          .questionPlaceholder=${t("createPost.faqQuestion")}
          .answerPlaceholder=${t("createPost.faqAnswer")}
          @question-change=${this.handleFaqQuestionChange}
          @answer-change=${this.handleFaqAnswerChange}
          @delete-requested=${this.handleFaqDeleteRequested}
        ></faq-block>
      `,
    );
  }

  private renderAddFaqButton() {
    return html` <button class="add-faq-link" @click=${this.addFaqBlock}>${t("createPost.addMoreFaq")}</button> `;
  }

  private renderSwitchesContainer() {
    return html`
      <div class="switches-container">
        ${this.renderWorldsSelect()}
        <div class="form-field">
          <div class="switch-list">
            <expandible-switch
              ?isExpanded=${this.switches.ctaButton}
              @expandedChange=${this.handleCtaExpandedChange}
              label="${t("createPost.addCtaButton")}"
              help-text="${t("createPost.addCtaButtonHelp")}"
            >
              <div slot="content" class="form-field">
                <wow-input
                  id="cta-call-to-action"
                  type="text"
                  .value=${this.ctaData.callToAction}
                  @input=${this.handleCtaCallToActionChange}
                  placeholder="${t("createPost.ctaCallToActionPlaceholder")}"
                ></wow-input>
              </div>
              <div slot="content" class="form-field">
                <wow-input
                  id="cta-url"
                  type="url"
                  .value=${this.ctaData.url}
                  @input=${this.handleCtaUrlChange}
                  placeholder="${t("createPost.ctaUrl")}"
                ></wow-input>
              </div>
            </expandible-switch>
            <expandible-switch
              ?isExpanded=${this.switches.discount}
              @expandedChange=${this.handleDiscountExpandedChange}
              label="${t("createPost.addDiscount")}"
              help-text="${t("createPost.addDiscountHelp")}"
            >
              <div slot="content" class="form-field">
                <wow-input
                  id="discount-code"
                  type="text"
                  .value=${this.discountData.code}
                  @input=${this.handleDiscountCodeChange}
                  placeholder="${t("createPost.discountCodePlaceholder")}"
                ></wow-input>
              </div>
            </expandible-switch>
            <expandible-switch
              ?isExpanded=${this.switches.brand}
              @expandedChange=${this.handleBrandExpandedChange}
              label="${t("createPost.addBrand")}"
              help-text="${t("createPost.addBrandHelp")}"
            >
              <div slot="content" class="form-field">
                <wow-autocomplete-multi-select
                  placeholder="${t("createPost.chooseBrands")}"
                  search-placeholder="${t("createPost.searchBrands")}"
                  loading-text="${t("createPost.loadingBrands")}"
                  error-text="${t("createPost.errorLoadingBrands")}"
                  no-options-text="${t("createPost.noBrandsAvailable")}"
                  no-results-text="${t("createPost.noBrandsFound")}"
                  .options=${this.brandOptions}
                  .loading=${this.brandsQuery.query.isActive() && this.brandsQuery.result.isPending}
                  .selectedValues=${this.selectedBrandIds}
                  .searchTerm=${this.brandSearchTerm}
                  @change=${this.handleBrandsChange}
                  @search=${this.handleBrandSearch}
                ></wow-autocomplete-multi-select>
              </div>
            </expandible-switch>
            <expandible-switch
              ?isExpanded=${this.switches.faq}
              @expandedChange=${this.handleFaqExpandedChange}
              label="${t("createPost.addFaq")}"
              help-text="${t("createPost.addFaqHelp")}"
            >
              <div slot="content" class="faq-blocks-container">
                ${this.renderFaqBlocks()} ${this.renderAddFaqButton()}
              </div>
            </expandible-switch>
          </div>
        </div>

        <!-- Premium Content Controls -->
        ${featureFlag(
          isStripePaywallEnabled(appContext.currentUser?.handle),
          html`
            <div class="form-field premium-section">
              <premium-content-controls
                .contentType=${"post"}
                .currentTier=${this.premiumTier}
                .hasTier=${!!this.subscriptionTierQuery.result.data?.tier}
                .oneTimePurchaseEnabled=${this.enableOneTimePurchase}
                .oneTimePriceCents=${this.oneTimePriceCents}
                @tier-change=${this.handleTierChange}
                @one-time-purchase-change=${this.handleOneTimePurchaseChange}
              ></premium-content-controls>
            </div>
          `,
        )}
      </div>
    `;
  }

  protected override render() {
    return html`
      <wow-modal
        variant="responsive"
        intercept-close
        mobile-fullscreen
        @close-requested=${this.handleCloseRequested}
        @footer-button-clicked=${this.handlePreviewPublish}
      >
        <span slot="header">${this.isEditMode ? t("editPost.title") : t("createPost.title")}</span>

        <div class="modal-content">
          <div class="form-layout">
            ${this.postId !== undefined && this.postQuery.result.isPending
              ? html`<p class="loading-text">${t("createPost.loadingPost")}</p>`
              : html`
                  <div class="left-column">${this.renderMediaUpload()} ${this.renderDescriptionField()}</div>

                  ${this.renderSwitchesContainer()}
                `}
          </div>
        </div>

        <span slot="footer-button-text"
          >${this.isEditMode ? t("editPost.saveChanges") : t("createPost.previewPublish")}</span
        >
      </wow-modal>

      <!-- Preview Modal (stacked over main modal) -->
      ${this.showPreviewModal && this.previewData
        ? html`
            <preview-publish-modal
              .previewData=${this.previewData}
              @close=${this.handlePreviewModalClose}
              @publish-success=${this.handlePublishSuccess}
            ></preview-publish-modal>
          `
        : nothing}

      <!-- Confirmation Dialog -->
      ${this.showCloseConfirmation
        ? html`
            <confirmation-edit-draft-discard
              @continue-editing=${this.handleContinueEditing}
              @confirmation-close=${this.handleConfirmationClose}
              @save-as-draft=${this.handleSaveAsDraft}
              @discard-changes=${this.handleDiscardChanges}
              .isSubmitting=${this.isSubmittingDraft}
              .header=${t("discounts.confirmation.stopTitle")}
            ></confirmation-edit-draft-discard>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "create-post-modal": CreatePostModal;
  }
}
